import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, MessageCircle, ArrowUpRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { copiarTexto } from "@/lib/clipboard";
import { WHATSAPP_BASE } from "@/lib/contacto";

const datos = [
  { id: "titular", label: "Titular", value: "Alvaro SILVA" },
  { id: "rut", label: "RUT", value: "20.999.678-2" },
  { id: "banco", label: "Banco", value: "Banco Falabella" },
  { id: "tipo", label: "Tipo de cuenta", value: "Cuenta Corriente" },
  { id: "cuenta", label: "Número de cuenta", value: "19811476704" },
  { id: "correo", label: "Correo", value: "alvarosilvagonzalez10@gmail.com" },
] as const;
const todosLosDatos = datos.map(({ label, value }) => `${label}: ${value}`).join("\n");
const comprobanteUrl = `${WHATSAPP_BASE}?text=${encodeURIComponent(
  "Hola, hice una transferencia a Pyme Visible. Te envío el comprobante de mi pedido.",
)}`;
const canonical = "https://pymevisible.cl/transferencia";
const title = "Datos de transferencia | Pyme Visible";
const description =
  "Copia los datos de transferencia de Pyme Visible y envía tu comprobante por WhatsApp.";

export const Route = createFileRoute("/transferencia")({
  component: TransferenciaPage,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: "noindex, nofollow, noarchive" },
      { name: "googlebot", content: "noindex, nofollow, noarchive" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: canonical },
      {
        property: "og:image",
        content: "https://pymevisible.cl/img/logotipo-pyme-visible-horizontal.png",
      },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: canonical }],
  }),
});

function TransferenciaPage() {
  const [copiado, setCopiado] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState("");
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiando = useRef(false);

  useEffect(
    () => () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    },
    [],
  );

  async function copiar(id: string, texto: string, label: string) {
    if (copiando.current) return;
    copiando.current = true;
    if (temporizador.current) clearTimeout(temporizador.current);
    try {
      await copiarTexto(texto);
      setCopiado(id);
      setMensaje(id === "todos" ? "Todos los datos copiados." : `${label} copiado.`);
      temporizador.current = setTimeout(() => setCopiado(null), 2500);
    } catch {
      setCopiado(null);
      setMensaje(
        "No se pudo copiar. Mantén presionado el dato o selecciónalo para copiarlo manualmente.",
      );
    } finally {
      copiando.current = false;
    }
  }

  return (
    <main className="pv-page-hero min-h-svh px-4 py-4 text-ink sm:px-6 sm:py-6">
      <div className="mx-auto max-w-[560px]">
        <a
          href="/"
          aria-label="Ir al inicio de Pyme Visible"
          className="mx-auto mb-4 block w-[180px] rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary sm:mb-5 sm:w-[210px]"
        >
          <ResponsiveImage
            src="/img/logotipo-pyme-visible-horizontal.png"
            alt="Pyme Visible"
            sizes="(max-width: 639px) 180px, 210px"
            loading="eager"
          />
        </a>

        <section
          aria-labelledby="transferencia-titulo"
          className="pv-surface-card relative overflow-hidden rounded-[1.75rem] p-5 hover:translate-y-0 sm:rounded-[2rem] sm:p-6"
        >
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-signal to-transparent"
          />
          <header>
            <p className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-primary">
              Tu pedido, un paso más cerca
            </p>
            <h1
              id="transferencia-titulo"
              className="mt-3 font-display text-[2.4rem] font-extrabold uppercase leading-[0.95] tracking-tight sm:text-[2.75rem]"
            >
              Datos de
              <br />
              transferencia
            </h1>
            <p className="mt-3 max-w-[38ch] text-sm leading-relaxed text-slate">
              Copia los datos y realiza la transferencia desde la app de tu banco.
            </p>
          </header>

          <button
            type="button"
            onClick={() => copiar("todos", todosLosDatos, "Datos")}
            className="pv-button pv-button--primary mt-5 flex min-h-12 w-full items-center justify-center gap-2 px-4 py-3 text-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
          >
            {copiado === "todos" ? (
              <Check size={18} aria-hidden="true" />
            ) : (
              <Copy size={18} aria-hidden="true" />
            )}
            {copiado === "todos" ? "Datos copiados" : "Copiar todos los datos"}
          </button>

          <dl className="mt-4 divide-y divide-ink/10">
            {datos.map(({ id, label, value }) => (
              <div key={id} className="flex items-center gap-2 py-1.5 sm:py-2">
                <div className="min-w-0 flex-1">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-slate">
                    {label}
                  </dt>
                  <dd
                    className={`mt-1 select-text break-words font-semibold leading-snug ${id === "correo" ? "text-[13px] sm:text-sm" : "text-base"}`}
                  >
                    {value}
                  </dd>
                </div>
                <button
                  type="button"
                  onClick={() => copiar(id, value, label)}
                  aria-label={`Copiar ${label.toLowerCase()}`}
                  title={`Copiar ${label.toLowerCase()}`}
                  className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-ink/10 bg-paper text-primary transition-colors hover:border-primary/30 hover:bg-sky focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {copiado === id ? (
                    <Check size={18} aria-hidden="true" />
                  ) : (
                    <Copy size={18} aria-hidden="true" />
                  )}
                </button>
              </div>
            ))}
          </dl>

          <p
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="mt-2 min-h-4 text-xs leading-4 text-primary"
          >
            {mensaje}
          </p>

          <div className="mt-2 border-t border-ink/10 pt-4">
            <a
              href={comprobanteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="pv-button pv-product-cta flex min-h-12 w-full items-center justify-center gap-2 px-3 py-3 text-center text-sm font-bold text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
            >
              <MessageCircle size={19} className="shrink-0" aria-hidden="true" />
              Enviar el comprobante por WhatsApp
              <ArrowUpRight size={16} className="hidden shrink-0 sm:block" aria-hidden="true" />
            </a>
            <p className="mt-2 text-center text-xs leading-relaxed text-slate">
              Se abrirá WhatsApp. Adjunta la imagen o el archivo de tu comprobante.
            </p>
          </div>
        </section>

        <p className="mx-auto mt-4 max-w-[42ch] text-center text-xs leading-relaxed text-slate">
          Antes de confirmar, verifica que el titular, el banco y el número de cuenta coincidan.
        </p>
        <footer className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.14em] text-slate">
          Pyme Visible · Hecho para tu negocio
        </footer>
      </div>
    </main>
  );
}
